import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { hackApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, Zap, ExternalLink, Target, User, CheckCircle, AlertTriangle, Wrench } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Hack } from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

export const HackDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skill, setSkill] = useState<Skill | null>(null);

  const canEdit = hasPermission('edit', 'hack');
  const canDelete = hasPermission('delete', 'hack');

  // Use entity hook for fetching hack
  const {
    data: hack,
    isLoading,
    error,
    refetch,
  } = useEntity<Hack>({
    fetchFn: (hackId: string) => hackApi.getById(hackId),
    entityId: id,
    entityName: 'hack',
    enabled: !!id,
  });

  // Fetch skill details if skillId exists
  useEffect(() => {
    if (!hack?.skillId) {
      setSkill(null);
      return;
    }

    skillApi.getById(hack.skillId)
      .then(setSkill)
      .catch((err) => {
        console.error('Error loading skill:', err);
        setSkill(null);
      });
  }, [hack?.skillId]);

  const isSystemDefined = hack?.hackGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'hack'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Hack ID is required');
      await hackApi.delete(id);
    },
    entityName: 'hack',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/hacks';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this hack?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading hack..." />;
  }

  if (error || !hack) {
    return <ErrorState error={error || new Error('Hack not found')} onRetry={refetch} />;
  }

  const hackId = hack.hackId || hack.id || '';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'SIMPLE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'MODERATE': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'COMPLEX': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ADVANCED': case 'EXPERT_LEVEL': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/hacks">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Zap className="h-6 w-6 text-orange-500" />
              <h1 className="text-3xl font-bold tracking-tight">{hack.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(hack.status)}`}>
                {hack.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(hack.complexity)}`}>
                {hack.complexity}
              </span>
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/hacks/${hackId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core hack details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={hackId} label="Hack ID" />
            {hack.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="mt-1 text-sm">{hack.description}</p>
              </div>
            )}
            {hack.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1 text-sm">{hack.summary}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content</label>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{hack.content}</p>
            </div>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                    {hack.category.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Hack Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                    {hack.hackType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            </div>
            {hack.subCategory && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sub Category</label>
                <p className="mt-1 text-sm">{hack.subCategory}</p>
              </div>
            )}
            {hack.implementationTimeframe && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Implementation Timeframe</label>
                <p className="mt-1 text-sm">{hack.implementationTimeframe}</p>
              </div>
            )}
            {hack.targetAudience && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Target Audience</label>
                <p className="mt-1 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{hack.targetAudience}</span>
                </p>
              </div>
            )}
            {hack.verificationStatus && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Verification Status</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded text-sm">
                    {hack.verificationStatus}
                  </span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Problem & Solution */}
        {(hack.problemStatement || hack.solution) && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Problem & Solution</CardTitle>
              <CardDescription>What this hack addresses and how</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              {hack.problemStatement && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Problem Statement
                  </label>
                  <p className="mt-2 text-sm bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">{hack.problemStatement}</p>
                </div>
              )}
              {hack.solution && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Solution
                  </label>
                  <p className="mt-2 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">{hack.solution}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Related Skill */}
        {hack.skillId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Related Skill
              </CardTitle>
              <CardDescription>Skill associated with this hack</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/skills/${hack.skillId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {skill?.name || 'View Skill'}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <CopyableId id={hack.skillId} label="Skill ID" />
                {skill?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                )}
                {skill?.category && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                    {skill.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metrics */}
        {(hack.effectivenessRating !== undefined || hack.popularityScore !== undefined || hack.successRate !== undefined) && (
          <Card>
            <CardHeader>
              <CardTitle>Metrics</CardTitle>
              <CardDescription>Performance and popularity metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {hack.effectivenessRating !== undefined && (
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{hack.effectivenessRating}</p>
                    <p className="text-sm text-muted-foreground">Effectiveness</p>
                  </div>
                )}
                {hack.popularityScore !== undefined && (
                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{hack.popularityScore}</p>
                    <p className="text-sm text-muted-foreground">Popularity</p>
                  </div>
                )}
                {hack.successRate !== undefined && (
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{hack.successRate}%</p>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Author Information */}
        {hack.authorInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Author Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {hack.authorInfo.authorName && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="mt-1 text-sm">{hack.authorInfo.authorName}</p>
                  </div>
                )}
                {hack.authorInfo.authorEmail && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="mt-1 text-sm">{hack.authorInfo.authorEmail}</p>
                  </div>
                )}
                {hack.authorInfo.authorId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Author ID</label>
                    <p className="mt-1 font-mono text-sm">{hack.authorInfo.authorId}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {hack.createdAt ? new Date(hack.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {hack.updatedAt ? new Date(hack.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {hack.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{hack.createdBy}</p>
                </div>
              )}
              {hack.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{hack.updatedBy}</p>
                </div>
              )}
              {hack.version !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="mt-1 text-sm">{hack.version}</p>
                </div>
              )}
              {hack.hackGenerated && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Generated</label>
                  <p className="mt-1 text-sm">{hack.hackGenerated.replace(/_/g, ' ')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Implementation Steps */}
      {hack.implementationSteps && hack.implementationSteps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Implementation Steps ({hack.implementationSteps.length})</CardTitle>
            <CardDescription>Step-by-step guide to implement this hack</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hack.implementationSteps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm pt-1">{step}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Required Tools */}
      {hack.requiredTools && hack.requiredTools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Required Tools ({hack.requiredTools.length})
            </CardTitle>
            <CardDescription>Tools needed to implement this hack</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hack.requiredTools.map((tool, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 text-sm"
                >
                  {tool}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prerequisites */}
      {hack.prerequisites && hack.prerequisites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Prerequisites ({hack.prerequisites.length})</CardTitle>
            <CardDescription>Requirements before implementing this hack</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hack.prerequisites.map((prereq, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-sm"
                >
                  {prereq}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benefits & Risks */}
      <div className="grid gap-6 md:grid-cols-2">
        {hack.benefits && hack.benefits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Benefits ({hack.benefits.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {hack.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {hack.risksLimitations && hack.risksLimitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="h-5 w-5" />
                Risks & Limitations ({hack.risksLimitations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {hack.risksLimitations.map((risk, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                    <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{risk}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Use Cases */}
      {hack.useCases && hack.useCases.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Use Cases ({hack.useCases.length})</CardTitle>
            <CardDescription>Scenarios where this hack is applicable</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {hack.useCases.map((useCase, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-sm">{useCase}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Examples */}
      {hack.examples && hack.examples.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Examples ({hack.examples.length})</CardTitle>
            <CardDescription>Practical examples of this hack</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hack.examples.map((example, idx) => (
                <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">{example}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resources */}
      {hack.resources && hack.resources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resources ({hack.resources.length})</CardTitle>
            <CardDescription>Additional resources</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hack.resources.map((resource, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 text-sm"
                >
                  {resource}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {hack.tags && hack.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({hack.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hack.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HackDetailPage;
